import React, { useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  IconArmchair2,
  IconBuildingWarehouse,
  IconChartArea,
  IconReportMoney,
  IconReceipt,
  IconTrashOff,
  IconChefHat,
  IconChevronLeft,
  IconChevronRight,
  IconDeviceIpadHorizontal,
  IconFileInvoice,
  IconFriends,
  IconLayoutDashboard,
  IconSettings2,
  IconStars,
  IconToolsKitchen3,
  IconUsersGroup,
  IconBed,
  IconBrush,
  IconBuildingBank,
  IconHanger,
  IconTruck,
  IconBox,
  IconScale,
  IconDiscount2,
  IconLayoutGrid,
} from "@tabler/icons-react";
import { clsx } from "clsx";
import Logo from "../assets/logo.svg";
import LogoDark from "../assets/LogoDark.svg"
import AvatarImg from "../assets/avatar.svg";
import { iconStroke } from "../config/config";
import { NavbarContext } from "../contexts/NavbarContext";
import { toggleNavbar } from "../helpers/NavbarSettings";
import { SCOPES } from "../config/scopes";
import { useTranslation } from "react-i18next";
import { useTheme } from "../contexts/ThemeContext";
import { useStoreSettings } from "../controllers/settings.controller";
import { getUserDetailsInLocalStorage, saveUserDetailsInLocalStorage } from "../helpers/UserDetails";

export default function Navbar() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [user, setUser] = React.useState(() => getUserDetailsInLocalStorage() || {});
  const { role: userRole, scope } = user;
  const userScopes = scope ? String(scope).split(",").map(s => s.trim()) : [];
  const { theme } = useTheme();
  const [isNavbarCollapsed, setIsNavbarCollapsed] = useContext(NavbarContext);
  const { data: storeSettings } = useStoreSettings();

  useEffect(() => {
    const handleAuthChange = () => {
      setUser(getUserDetailsInLocalStorage() || {});
    };
    window.addEventListener("auth_state_changed", handleAuthChange);
    return () => window.removeEventListener("auth_state_changed", handleAuthChange);
  }, []);

  useEffect(() => {
    if (storeSettings?.enabled_modules !== undefined && storeSettings?.enabled_modules !== null && storeSettings.enabled_modules !== user.enabled_modules) {
      const updatedUser = {
        ...user,
        enabled_modules: storeSettings.enabled_modules
      };
      saveUserDetailsInLocalStorage(updatedUser);
      setUser(updatedUser);
    }
  }, [storeSettings?.enabled_modules]);

  const navbarItems = [
    {
      type: "text",
      text: "Core Operations",
    },
    {
      type: "link",
      text: t("navbar.dashboard"),
      icon: <IconLayoutDashboard stroke={iconStroke} />,
      path: "/dashboard/home",
      scopes: [SCOPES.DASHBOARD]
    },
    {
      type: "link",
      text: t("navbar.pos"),
      icon: <IconDeviceIpadHorizontal stroke={iconStroke} />,
      path: "/dashboard/pos",
      scopes: [SCOPES.POS],
      module: "pos"
    },
    {
      type: "link",
      text: t("navbar.orders"),
      icon: <IconToolsKitchen3 stroke={iconStroke} />,
      path: "/dashboard/orders",
      scopes: [SCOPES.POS, SCOPES.ORDERS, SCOPES.ORDER_STATUS, SCOPES.ORDER_STATUS_DISPLAY],
      module: "orders"
    },
    {
      type: "link",
      text: "Delivery & Rooms",
      icon: <IconTruck stroke={iconStroke} />,
      path: "/dashboard/requests",
      scopes: [SCOPES.POS, SCOPES.ORDERS, SCOPES.PMS, SCOPES.ROOMS],
      module: "requests"
    },
    {
      type: "link",
      text: t("navbar.kitchen"),
      icon: <IconChefHat stroke={iconStroke} />,
      path: "/dashboard/kitchen",
      scopes: [SCOPES.KITCHEN, SCOPES.KITCHEN_DISPLAY],
      module: "kitchen"
    },
    {
      type: "link",
      text: t("navbar.reservation"),
      icon: <IconArmchair2 stroke={iconStroke} />,
      path: "/dashboard/reservation",
      scopes: [SCOPES.RESERVATIONS, SCOPES.VIEW_RESERVATIONS, SCOPES.MANAGE_RESERVATIONS],
      module: "reservation"
    },
    {
      type: "link",
      text: t("navbar.customers"),
      icon: <IconFriends stroke={iconStroke} />,
      path: "/dashboard/customers",
      scopes: [SCOPES.CUSTOMERS, SCOPES.VIEW_CUSTOMERS, SCOPES.MANAGE_CUSTOMERS],
      module: "customers"
    },
    {
      type: "text",
      text: "PMS Operations",
      module: "pms"
    },
    {
      type: "link",
      text: "Room Status Grid",
      icon: <IconBed stroke={iconStroke} />,
      path: "/dashboard/pms/rooms",
      scopes: [SCOPES.PMS, SCOPES.ROOMS],
      module: "pms"
    },
    {
      type: "link",
      text: "Housekeeping",
      icon: <IconBrush stroke={iconStroke} />,
      path: "/dashboard/pms/housekeeping",
      scopes: [SCOPES.PMS, SCOPES.HOUSEKEEPING],
      module: "pms"
    },
    {
      type: "link",
      text: "Laundry & Linen",
      icon: <IconHanger stroke={iconStroke} />,
      path: "/dashboard/pms/laundry",
      scopes: [SCOPES.PMS, SCOPES.LAUNDRY],
      module: "pms"
    },
    {
      type: "text",
      text: "Inventory & Supply",
    },
    {
      type: "link",
      text: "Inventory",
      icon: <IconBuildingWarehouse stroke={iconStroke} />,
      path: "/dashboard/inventory",
      scopes: [SCOPES.INVENTORY, SCOPES.VIEW_INVENTORY, SCOPES.MANAGE_INVENTORY],
      module: "inventory"
    },
    {
      type: "link",
      text: "Purchases",
      icon: <IconBuildingWarehouse stroke={iconStroke} />,
      path: "/dashboard/inventory/purchases",
      scopes: [SCOPES.INVENTORY, SCOPES.PURCHASES, SCOPES.PURCHASE_INVOICES],
      module: "inventory"
    },
    {
      type: "link",
      text: "Suppliers",
      icon: <IconUsersGroup stroke={iconStroke} />,
      path: "/dashboard/inventory/suppliers",
      scopes: [SCOPES.INVENTORY, SCOPES.SUPPLIERS],
      module: "inventory"
    },
    {
      type: "link",
      text: "Expenses",
      icon: <IconFileInvoice stroke={iconStroke} />,
      path: "/dashboard/expenses",
      scopes: [SCOPES.EXPENSES, SCOPES.REPORTS, SCOPES.INVENTORY],
      module: "inventory"
    },
    {
      type: "text",
      text: "Financial Control",
    },
    {
      type: "link",
      text: "Shift Control",
      icon: <IconCashBanknote stroke={iconStroke} />,
      path: "/dashboard/financial/shifts",
      scopes: [SCOPES.POS, SCOPES.REPORTS]
    },
    {
      type: "link",
      text: "Register",
      icon: <IconReportAnalytics stroke={iconStroke} />,
      path: "/dashboard/financial/registers",
      scopes: [SCOPES.REPORTS]
    },
    {
      type: "link",
      text: "Day Book",
      icon: <IconReportMoney stroke={iconStroke} />,
      path: "/dashboard/financial/daybook",
      scopes: [SCOPES.REPORTS]
    },
    {
      type: "text",
      text: "Accounting & Finance",
    },
    {
      type: "link",
      text: "Bank Accounts",
      icon: <IconBuildingBank stroke={iconStroke} />,
      path: "/dashboard/accounting/banks",
      scopes: [SCOPES.ACCOUNTING, SCOPES.INVOICES, SCOPES.SETTINGS],
      module: "accounting_banks"
    },
    {
      type: "link",
      text: t("navbar.invoices"),
      icon: <IconFileInvoice stroke={iconStroke} />,
      path: "/dashboard/invoices",
      scopes: [SCOPES.INVOICES],
      module: "invoices"
    },
    {
      type: "link",
      text: "Accounts Receivable",
      icon: <IconBuildingWarehouse stroke={iconStroke} />,
      path: "/dashboard/accounts-receivable",
      scopes: [SCOPES.INVOICES, SCOPES.ACCOUNTING, SCOPES.REPORTS],
      module: "accounts_receivable"
    },
    {
      type: "link",
      text: "Accounts Payable",
      icon: <IconBuildingWarehouse stroke={iconStroke} />,
      path: "/dashboard/inventory/accounts-payable",
      scopes: [SCOPES.INVENTORY, SCOPES.PURCHASES, SCOPES.PURCHASE_INVOICES, SCOPES.ACCOUNTING],
      module: "accounts_payable"
    },
    {
      type: "text",
      text: "Reports & Analytics",
      module: "reports"
    },
    {
      type: "link",
      text: "General Ledger",
      icon: <IconChartArea stroke={iconStroke} />,
      path: "/dashboard/reports/ledger",
      scopes: [SCOPES.REPORTS],
      module: "report_ledger"
    },
    {
      type: "link",
      text: "Trial Balance",
      icon: <IconScale stroke={iconStroke} />,
      path: "/dashboard/reports/trial-balance",
      scopes: [SCOPES.REPORTS],
      module: "report_trialbalance"
    },
    {
      type: "link",
      text: "Balance Sheet",
      icon: <IconBuildingBank stroke={iconStroke} />,
      path: "/dashboard/reports/balance-sheet",
      scopes: [SCOPES.REPORTS],
      module: "report_balancesheet"
    },

    {
      type: "link",
      text: "All Reports & Analytics",
      icon: <IconLayoutGrid stroke={iconStroke} />,
      path: "/dashboard/reports",
      scopes: [SCOPES.REPORTS],
      module: "reports"
    },

    {
      type: "text",
      text: "Asset Management",
    },
    {
      type: "link",
      text: "Hotel Assets",
      icon: <IconBox stroke={iconStroke} />,
      path: "/dashboard/assets",
      scopes: [SCOPES.ASSETS, SCOPES.SETTINGS, SCOPES.INVENTORY],
      module: "assets"
    },
    {
      type: "text",
      text: t("navbar.back_office"),
    },
    {
      type: "link",
      text: t("navbar.feedbacks"),
      icon: <IconStars stroke={iconStroke} />,
      path: "/dashboard/feedbacks",
      scopes: [SCOPES.FEEDBACK],
      module: "feedbacks"
    },
    {
      type: "link",
      text: t("navbar.users"),
      icon: <IconUsersGroup stroke={iconStroke} />,
      path: "/dashboard/users",
      scopes: [SCOPES.SETTINGS]
    },
    {
      type: "link",
      text: "HR & Payroll",
      icon: <IconUsersGroup stroke={iconStroke} />,
      path: "/dashboard/payroll",
      scopes: [SCOPES.PAYROLL, SCOPES.SETTINGS],
      module: "hr"
    },
    {
      type: "link",
      text: t("navbar.settings"),
      icon: <IconSettings2 stroke={iconStroke} />,
      path: "/dashboard/settings",
      scopes: [SCOPES.SETTINGS]
    },
  ];

  const isModuleEnabled = (moduleKey) => {
    if (!moduleKey) return true;
    const modulesStr = (storeSettings?.enabled_modules !== undefined && storeSettings?.enabled_modules !== null)
      ? storeSettings.enabled_modules
      : user.enabled_modules;

    if (modulesStr === undefined || modulesStr === null) return true;
    const enabledList = modulesStr.split(",").map(m => m.trim());

    if (moduleKey === "requests") {
      return enabledList.includes("requests") || enabledList.includes("orders") || enabledList.includes("pos");
    }

    if (moduleKey === "report_order_deletion" && (enabledList.includes("report_order_deletion") || enabledList.includes("report_orderdeletion"))) {
      return true;
    }

    if (moduleKey === "report_discounts" || moduleKey === "report_discounts_deductions") {
      return enabledList.includes("report_discounts") || enabledList.includes("report_discounts_deductions") || enabledList.includes("reports");
    }

    if (moduleKey.startsWith("report_")) {
      if (enabledList.includes(moduleKey)) return true;
      if (enabledList.includes("reports")) return true;
    }

    if (moduleKey === "assets" || moduleKey === "hotel_assets") {
      return enabledList.includes("assets") || enabledList.includes("hotel_assets");
    }

    if (moduleKey === "accounts_payable") {
      return enabledList.includes("accounts_payable") || enabledList.includes("inventory") || enabledList.includes("accounting") || enabledList.includes("accounting_banks");
    }

    if (moduleKey === "accounts_receivable") {
      return enabledList.includes("accounts_receivable") || enabledList.includes("invoices") || enabledList.includes("accounting") || enabledList.includes("accounting_banks");
    }

    return enabledList.includes(moduleKey);
  };


  const filteredNavbarItems = [];

  const visibleLinks = navbarItems.filter(item => {
    if (item.type !== "link") return false;
    if (!isModuleEnabled(item.module)) return false;
    if (userRole === "admin") return true;
    return item.scopes && item.scopes.length > 0
      ? item.scopes.some(scope => userScopes && userScopes.includes(scope))
      : false;
  });

  navbarItems.forEach(item => {
    if (item.type === "link") {
      const isVisible = visibleLinks.some(link => link.path === item.path);
      if (isVisible) {
        filteredNavbarItems.push(item);
      }
    } else if (item.type === "text") {
      let hasVisibleChild = false;
      if (item.text === "Core Operations") {
        hasVisibleChild = visibleLinks.some(link => ["pos", "orders", "kitchen", "reservation", "customers", "requests", undefined].includes(link.module));
      } else if (item.text === "PMS Operations") {
        hasVisibleChild = visibleLinks.some(link => link.module === "pms");
      } else if (item.text === "Inventory & Supply") {
        hasVisibleChild = visibleLinks.some(link => ["inventory"].includes(link.module) || link.path.startsWith("/dashboard/inventory") || link.path === "/dashboard/expenses");
      } else if (item.text === "Accounting & Finance") {
        hasVisibleChild = visibleLinks.some(link => ["accounting_banks", "invoices", "accounts_receivable", "accounts_payable"].includes(link.module) || link.path.startsWith("/dashboard/accounting") || link.path.startsWith("/dashboard/invoices") || link.path.startsWith("/dashboard/accounts-receivable"));
      } else if (item.text === "Asset Management") {
        hasVisibleChild = visibleLinks.some(link => link.module === "assets" || link.module === "hotel_assets" || link.path === "/dashboard/assets");
      } else if (item.text === "Reports & Analytics") {
        hasVisibleChild = visibleLinks.some(link => link.module && (link.module.startsWith("report_") || link.module === "reports"));
      } else if (item.text === t("navbar.back_office")) {
        hasVisibleChild = visibleLinks.some(link => ["feedbacks", "hr"].includes(link.module) || link.path === "/dashboard/users" || link.path === "/dashboard/settings" || link.path === "/dashboard/payroll" || link.path === "/dashboard/feedbacks");
      }
      if (hasVisibleChild) {
        filteredNavbarItems.push(item);
      }
    }
  });

  const btnToggleNavbar = () => {
    const isNavCollapsed = toggleNavbar();
    console.log(isNavCollapsed);
    if (isNavCollapsed) {
      setIsNavbarCollapsed(true);
    } else {
      setIsNavbarCollapsed(false);
    }
  };

  if (isNavbarCollapsed) {
    return (
       <div className ="flex flex-col items-start gap-4 h-screen px-5 py-6 overflow-y-auto fixed left-0 top-0 bg-restro-green-light">

      <img src={theme === "black" ? LogoDark : Logo} alt="logo" className="w-12 block mb-6" />
        {filteredNavbarItems.map((item, index) => {
          if (item.type == "text") {
            return null;
          }

          return (
            <Link
              key={index}
              className={clsx(
                `w-12 h-12 flex items-center justify-center rounded-full transition`,
                {
                  "bg-restro-bg-hover-dark-mode font-medium text-white": theme === 'black' && pathname.includes(item.path),
                  "bg-restro-border-green-light font-medium text-black": theme !== 'black' && pathname.includes(item.path),
                  "hover:bg-restro-bg-hover-dark-mode": theme === 'black' && !pathname.includes(item.path),
                  "hover:bg-restro-border-green-light": theme !== 'black' && !pathname.includes(item.path),
                }
              )}
              to={item.path}
            >
              {React.cloneElement(item.icon, {
                className: clsx(
                  "transition-colors text-current",
                  {
                    'text-white': theme === 'black' && pathname.includes(item.path),
                    'text-black': theme !== 'black' && pathname.includes(item.path),
                  }
                ),
              })}
            </Link>
          );
        })}

        <button
          onClick={btnToggleNavbar}
          className="w-12 h-12 flex items-center justify-center rounded-full transitionborder border-restro-green-light hover:bg-restro-border-green text-restro-text"
        >
          <IconChevronRight stroke={iconStroke} />
        </button>
      </div>
    );
  }

  return (
    <div className="relative h-screen">
      <div className="flex flex-col items-start gap-2 md:w-72  md:gap-3 h-screen px-5 py-6 overflow-y-auto fixed left-0 top-0 bg-restro-green-light">
        <img src={theme === 'black' ? LogoDark : Logo } alt="logo" className="block w-12 md:w-auto md:h-14 mb-2 md:mb-6"/>

        <div className="hidden md:flex items-center gap-2 w-full md:mb-6">
          <img
            src={AvatarImg}
            alt="avatar"
            className="md:w-12 md:h-12 rounded-full block"
          />
          <div>
            <p className="font-medium">{user.name}</p>
            <p className="text-xs text-gray-500">
              {new String(user.role).toUpperCase()}
              {user.designation && <span>, {user.designation}</span>}
            </p>
          </div>
        </div>

        {filteredNavbarItems.map((item, index) => {
          if (item.type == "text") {
            return (
              <p key={index} className="font-bold hidden md:block">
                {item.text}
              </p>
            );
          }

          return (

          <Link
            key={index}
            to={item.path}
            className={clsx(
              `w-12 h-12 md:w-full flex justify-center md:justify-normal items-center md:gap-1 md:px-4 md:py-3 rounded-full transition group`,
              {
                'bg-restro-border-green-light font-medium': theme !== 'black' && pathname.includes(item.path),
                'bg-restro-bg-hover-dark-mode font-medium text-white': theme === 'black' && pathname.includes(item.path),
                'hover:bg-restro-bg-hover-dark-mode hover:text-white': theme === 'black' && !pathname.includes(item.path),
                'hover:bg-restro-border-green-light': theme !== 'black' && !pathname.includes(item.path),
                'text-white': theme === 'black' && !pathname.includes(item.path),
                'text-black': theme === 'black' && pathname.includes(item.path),
                'text-restro-text-light-mode hover:text-black': theme !== 'black',
              }
            )}
          >
            {React.cloneElement(item.icon, {
              className: clsx(
                'transition-colors',
                {
                  'text-white group-hover:text-white hover:text-white': theme === 'black',
                  'text-black': theme !== 'black',
                }
              ),
            })}
            <p
              className={clsx(
                'hidden md:block transition-colors',
                {
                  'text-white group-hover:text-white hover:text-white': theme === 'black',
                  'text-gray-900 group-hover:text-black': theme !== 'black',
                  'text-black': theme !== 'black' && pathname.includes(item.path),
                }
              )}
            >
              {item.text}
            </p>
          </Link>

          );
        })}
      </div>

      <button
        onClick={btnToggleNavbar}
        className="w-9 h-9 hidden md:flex items-center justify-center rounded-full border transition bg-restro-green-light border-restro-border-green dark:bg-restro-gray hover:bg-gray-100 dark:hover:bg-restro-button-hover text-gray-500 fixed bottom-4 left-[17.5rem] -translate-x-1/2"
      >
        <IconChevronLeft stroke={iconStroke} size={18} />
      </button>
    </div>
  );
}
